import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Progress,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdArrowBack, MdAdd, MdEdit, MdDeleteOutline } from 'react-icons/md';
import { FiFileText, FiUploadCloud, FiX } from 'react-icons/fi';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { format, isAfter, startOfToday } from 'date-fns';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import AiAssistantDrawer from 'domains/ai/components/AiAssistantDrawer';
import { GOOGLE_MAPS_CONFIG } from 'config/googleMaps';
import { useTranslation } from 'i18n/TranslationProvider';
import API from 'api/api';
import { getPublicSettings } from 'services/settingsService';

import { useListing } from 'contexts/ListingContext';
import ListingItemsSection from 'domains/listing/components/ListingItemsSection';
import { consumeClientListingPrefill } from 'domains/ai/utils/storage';
import {
  createListingDraft,
  publishListingDraft,
  updateListing as updateListingRequest,
  uploadListingImage,
} from '../services/listingService';
import Map from 'components/map/components/StaticMap';
import { CATEGORIES } from 'domains/shared/utils/categoryUtils';
import {
  isRequiredField,
} from '../constants/categoryMetadata';
import {
  validateItemsForCategory,
  formatValidationErrors,
} from '../utils/itemValidation';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCENT = 'brand.500';
const ACCENT_SUBTLE_BG = 'secondaryGray.300';
const ACCENT_BORDER = 'brand.300';

function formatFileSize(size = 0) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIdentity(file) {
  return `${file?.name || ''}:${file?.size || 0}:${file?.lastModified || 0}`;
}

// Convert a single wizard item into the shape the backend schema expects.
// The wizard historically stored flat legacy keys (length/width/height,
// volume, status, petDescription) that don't exist on the Mongoose itemSchema.
// Normalize them here so the payload matches the schema and passes validation.
function normalizeItemForPayload(item = {}) {
  const out = { ...item };

  // Vehicle status: wizard used `status`, backend expects `vehicleStatus`.
  if (item.status && !item.vehicleStatus) {
    out.vehicleStatus = item.status;
  }
  delete out.status;

  // Dimensions: wizard used flat length/width/height (cm).
  if (item.length || item.width || item.height) {
    out.dimensions = {
      l: item.length ? Number(item.length) : undefined,
      w: item.width ? Number(item.width) : undefined,
      h: item.height ? Number(item.height) : undefined,
    };
  }
  delete out.length;
  delete out.width;
  delete out.height;

  // Removals: wizard used `volume`, backend expects `totalVolumeCbm`.
  if (item.volume && !item.totalVolumeCbm) {
    out.totalVolumeCbm = Number(item.volume);
  }
  delete out.volume;

  // Pets: wizard used `petDescription`, backend expects `itemDescription`.
  if (item.petDescription && !out.itemDescription) {
    out.itemDescription = item.petDescription;
  }
  delete out.petDescription;

  // Coerce numeric string inputs (Chakra Input returns strings).
  const numericKeys = [
    'weight',
    'quantity',
    'year',
    'lengthMeters',
    'totalVolumeCbm',
    'originFloor',
    'destFloor',
  ];
  numericKeys.forEach((key) => {
    if (out[key] !== undefined && out[key] !== '' && out[key] !== null) {
      const num = Number(out[key]);
      if (Number.isFinite(num)) out[key] = num;
    }
  });

  return out;
}

function buildDraftPayload(listingData = {}) {
  const {
    category,
    fromAddress,
    fromCity,
    fromRegion,
    fromCountry,
    fromPostcode,
    fromCoords,
    toAddress,
    toCity,
    toRegion,
    toCountry,
    toPostcode,
    toCoords,
    flexiblePickup,
    flexibleDelivery,
    pickupDateFrom,
    pickupDateTo,
    deliveryDateFrom,
    deliveryDateTo,
    items,
    clientType,
    companyCountry,
    allowsQuotes,
    clientPriceGross,
    description,
    title,
  } = listingData;

  return {
    category,
    fromAddress,
    fromCity,
    fromRegion,
    fromCountry,
    fromPostcode,
    fromCoords,
    toAddress,
    toCity,
    toRegion,
    toCountry,
    toPostcode,
    toCoords,
    flexiblePickup,
    flexibleDelivery,
    pickupDateFrom,
    pickupDateTo,
    deliveryDateFrom,
    deliveryDateTo,
    items: Array.isArray(items) ? items.map(normalizeItemForPayload) : items,
    clientType,
    companyCountry,
    allowsQuotes,
    clientPriceGross: clientPriceGross === '' ? null : clientPriceGross,
    description,
    title,
    images: [],
  };
}

function extractAddressPart(place, type) {
  return (
    place?.address_components?.find((component) =>
      component.types.includes(type),
    )?.long_name || ''
  );
}

function estimateDistanceKm(origin, destination) {
  if (
    origin?.lat == null ||
    origin?.lng == null ||
    destination?.lat == null ||
    destination?.lng == null
  ) {
    return null;
  }
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRadians(destination.lat - origin.lat);
  const dLng = toRadians(destination.lng - origin.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(origin.lat)) *
      Math.cos(toRadians(destination.lat)) *
      Math.sin(dLng / 2) ** 2;
  return Math.round(
    earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)),
  );
}

function formatCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return `EUR ${numeric.toFixed(2)}`;
}


// ---------------------------------------------------------------------------
// Step progress indicator
// ---------------------------------------------------------------------------

function StepDots({ current, total }) {
  return (
    <HStack spacing={2} justify="center" mb={6}>
      {Array.from({ length: total }).map((_, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <Box
            key={step}
            w={active ? '28px' : '10px'}
            h="10px"
            borderRadius="full"
            bg={done || active ? ACCENT : 'gray.200'}
            transition="all 0.25s"
          />
        );
      })}
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Select Category
// ---------------------------------------------------------------------------

function Step1Category() {
  const { listingData, updateListing, setCurrentStep } = useListing();
  const { t } = useTranslation();
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderDefault = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const catItemColor = useColorModeValue('gray.700', 'white');

  const handleSelect = (cat) => {
    updateListing({ category: cat.id });
    setCurrentStep(2);
  };

  return (
    <Box>
      <Heading size="lg" fontWeight="800" mb={1}>
        {t('Select a category')}
      </Heading>
      <Text color={mutedText} mb={8} fontSize="md">
        {t('You will receive the best transport quotes and save up to 60%!')}
      </Text>

      <Grid
        templateColumns={{
          base: 'repeat(3, 1fr)',
          sm: 'repeat(4, 1fr)',
          md: 'repeat(5, 1fr)',
          lg: 'repeat(7, 1fr)',
        }}
        gap={3}
      >
        {CATEGORIES.map((cat) => {
          const selected = listingData.category === cat.id;
          return (
            <GridItem key={cat.id}>
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap={2}
                p={3}
                bg={selected ? ACCENT_SUBTLE_BG : cardBg}
                border="2px solid"
                borderColor={selected ? ACCENT_BORDER : borderDefault}
                borderRadius="xl"
                cursor="pointer"
                transition="all 0.15s"
                _hover={{ borderColor: ACCENT_BORDER, bg: ACCENT_SUBTLE_BG }}
                onClick={() => handleSelect(cat)}
                minH="88px"
                textAlign="center"
              >
                <Text fontSize="2xl" lineHeight="1">
                  {cat.emoji}
                </Text>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color={selected ? ACCENT : catItemColor}
                  lineHeight="1.2"
                >
                  {t(cat.label)}
                </Text>
              </Flex>
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Choose Route
// ---------------------------------------------------------------------------

function Step2Route({ isGoogleLoaded, addressAutocompleteEnabled = true }) {
  const { listingData, updateListing, setCurrentStep } = useListing();
  const { t } = useTranslation();
  const toast = useToast();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('white', 'navy.800');
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const infoBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const infoBorderColor = useColorModeValue('blue.100', 'whiteAlpha.200');
  const hasRoutePreview = Boolean(
    listingData.fromCoords && listingData.toCoords,
  );
  const estimatedDistanceKm = useMemo(
    () => estimateDistanceKm(listingData.fromCoords, listingData.toCoords),
    [listingData.fromCoords, listingData.toCoords],
  );

  const [routeDistanceKm, setRouteDistanceKm] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const originCoords = listingData.fromCoords;
  const destinationCoords = listingData.toCoords;

  // Ref keeps current city/country for the cache key without triggering re-fetches on keystroke
  const routeContextRef = useRef({});
  routeContextRef.current = {
    fromCity: listingData.fromCity,
    fromCountry: listingData.fromCountry,
    toCity: listingData.toCity,
    toCountry: listingData.toCountry,
  };

  useEffect(() => {
    if (!originCoords || !destinationCoords) {
      setRouteDistanceKm(null);
      return;
    }
    setDistanceLoading(true);
    const ctx = routeContextRef.current;
    API.post('/listings/route-distance', {
      fromLat: originCoords.lat,
      fromLng: originCoords.lng,
      toLat: destinationCoords.lat,
      toLng: destinationCoords.lng,
      fromCity: ctx.fromCity,
      fromCountry: ctx.fromCountry,
      toCity: ctx.toCity,
      toCountry: ctx.toCountry,
    })
      .then((r) => setRouteDistanceKm(r.data.distanceKm))
      .catch(() => setRouteDistanceKm(null))
      .finally(() => setDistanceLoading(false));
  }, [destinationCoords, originCoords]);
  // Restrict predictions to European markets LogiFlow serves. Keep in sync with
  // mobile's COUNTRY_RESTRICTION in address-autocomplete-field.tsx.
  const autocompleteOptions = {
    fields: ['address_components', 'formatted_address', 'geometry'],
    types: ['address'],
    componentRestrictions: {
      country: [
        'bg', 'ro', 'gr', 'de', 'fr', 'it', 'es', 'pl',
        'nl', 'at', 'hu', 'cz', 'sk', 'si', 'hr', 'rs',
      ],
    },
  };
  const fromAutocompleteRef = useRef(null);
  const toAutocompleteRef = useRef(null);
  // Signature-gated geocode cache: prevents redundant Geocoder calls when onBlur fires
  // with unchanged address text. Massive cost saver on tab-through flows.
  const lastGeocodedSignatureRef = useRef({ from: '', to: '' });

  const [showDates, setShowDates] = useState(
    !!(listingData.pickupDateFrom || listingData.deliveryDateFrom),
  );

  const geocodeAddress = async (parts) => {
    if (!isGoogleLoaded || !window.google?.maps) return null;

    const address = [parts.address, parts.city, parts.postcode, parts.country]
      .filter(Boolean)
      .join(', ')
      .trim();

    if (!address) return null;

    const geocoder = new window.google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status !== 'OK' || !results?.[0]?.geometry?.location) {
          resolve(null);
          return;
        }

        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng(),
        });
      });
    });
  };

  const syncPlaceToListing = (type, place) => {
    if (!place?.geometry?.location) return false;

    const prefix = type === 'from' ? 'from' : 'to';
    const city =
      extractAddressPart(place, 'locality') ||
      extractAddressPart(place, 'postal_town') ||
      extractAddressPart(place, 'administrative_area_level_2');
    const postcode = extractAddressPart(place, 'postal_code');
    const country = extractAddressPart(place, 'country');
    const region = extractAddressPart(place, 'administrative_area_level_1');

    updateListing({
      [`${prefix}Address`]:
        place.formatted_address || listingData[`${prefix}Address`] || '',
      [`${prefix}City`]: city || listingData[`${prefix}City`] || '',
      [`${prefix}Postcode`]: postcode || listingData[`${prefix}Postcode`] || '',
      [`${prefix}Country`]: country || listingData[`${prefix}Country`] || '',
      [`${prefix}Region`]: region || listingData[`${prefix}Region`] || '',
      [`${prefix}Coords`]: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
    });

    return true;
  };

  const handlePlaceChanged = (type) => {
    const autocomplete =
      type === 'from' ? fromAutocompleteRef.current : toAutocompleteRef.current;
    const place = autocomplete?.getPlace();
    syncPlaceToListing(type, place);
  };

  const resolveManualCoords = async (type) => {
    // When autocomplete is disabled platform-wide, we also skip client-side
    // geocoding — the backend's HereMaps enrichment will resolve coords on publish.
    if (!addressAutocompleteEnabled) return;

    const prefix = type === 'from' ? 'from' : 'to';
    const coordsKey = `${prefix}Coords`;

    // Skip redundant Geocoder calls: coords already present, or nothing changed
    // since the last successful geocode for this address.
    if (listingData[coordsKey]) return;

    const signature = [
      listingData[`${prefix}Address`],
      listingData[`${prefix}City`],
      listingData[`${prefix}Postcode`],
      listingData[`${prefix}Country`],
    ]
      .map((v) => (v || '').trim().toLowerCase())
      .join('|');

    if (lastGeocodedSignatureRef.current[prefix] === signature) return;
    if (!signature.replace(/\|/g, '')) return; // all blank

    const nextCoords = await geocodeAddress({
      address: listingData[`${prefix}Address`],
      city: listingData[`${prefix}City`],
      postcode: listingData[`${prefix}Postcode`],
      country: listingData[`${prefix}Country`],
    });

    if (nextCoords) {
      lastGeocodedSignatureRef.current[prefix] = signature;
      updateListing({ [coordsKey]: nextCoords });
    }
  };

  const handleNext = async () => {
    if (
      !listingData.fromCity ||
      !listingData.fromCountry ||
      !listingData.toCity ||
      !listingData.toCountry
    )
      return;
    if (!listingData.fromCoords) {
      await resolveManualCoords('from');
    }
    if (!listingData.toCoords) {
      await resolveManualCoords('to');
    }
    setCurrentStep(3);
  };

  return (
    <Box>
      <Heading size="lg" fontWeight="800" mb={1}>
        {t('Choose a route')}
      </Heading>
      <Text color={mutedText} mb={8} fontSize="md">
        {t('Enter pick-up and delivery locations.')}
      </Text>

      <Flex direction={{ base: 'column', lg: 'row' }} gap={6} align="stretch">
        {/* LEFT: form */}
        <Box flex="1" minW={0}>
          {/* From */}
          <Box mb={5}>
            <Text fontWeight="700" color={labelColor} mb={2} fontSize="sm">
              {t('Pick-up location')}
            </Text>
            {isGoogleLoaded && addressAutocompleteEnabled ? (
              <Autocomplete
                options={autocompleteOptions}
                onLoad={(instance) => {
                  fromAutocompleteRef.current = instance;
                }}
                onPlaceChanged={() => handlePlaceChanged('from')}
              >
                <Input
                  bg={inputBg}
                  placeholder={t('Street address')}
                  value={listingData.fromAddress || ''}
                  onChange={(e) =>
                    updateListing({
                      fromAddress: e.target.value,
                      fromCoords: null,
                    })
                  }
                  onBlur={() => resolveManualCoords('from')}
                  borderRadius="lg"
                  size="md"
                  mb={2}
                />
              </Autocomplete>
            ) : (
              <Input
                bg={inputBg}
                placeholder={t('Street address')}
                value={listingData.fromAddress || ''}
                onChange={(e) =>
                  updateListing({
                    fromAddress: e.target.value,
                    fromCoords: null,
                  })
                }
                borderRadius="lg"
                size="md"
                mb={2}
              />
            )}
            <HStack spacing={2}>
              <Input
                bg={inputBg}
                placeholder={t('City *')}
                value={listingData.fromCity || ''}
                onChange={(e) =>
                  updateListing({ fromCity: e.target.value, fromCoords: null })
                }
                onBlur={() => resolveManualCoords('from')}
                size="sm"
                borderRadius="lg"
                flex="2"
              />
              <Input
                bg={inputBg}
                placeholder={t('Postcode')}
                value={listingData.fromPostcode || ''}
                onChange={(e) =>
                  updateListing({
                    fromPostcode: e.target.value,
                    fromCoords: null,
                  })
                }
                onBlur={() => resolveManualCoords('from')}
                size="sm"
                borderRadius="lg"
                flex="1"
              />
              <Input
                bg={inputBg}
                placeholder={t('Country *')}
                value={listingData.fromCountry || ''}
                onChange={(e) =>
                  updateListing({
                    fromCountry: e.target.value,
                    fromCoords: null,
                  })
                }
                onBlur={() => resolveManualCoords('from')}
                size="sm"
                borderRadius="lg"
                flex="2"
              />
            </HStack>
          </Box>

          {/* To */}
          <Box mb={6}>
            <Text fontWeight="700" color={labelColor} mb={2} fontSize="sm">
              {t('Delivery location')}
            </Text>
            {isGoogleLoaded && addressAutocompleteEnabled ? (
              <Autocomplete
                options={autocompleteOptions}
                onLoad={(instance) => {
                  toAutocompleteRef.current = instance;
                }}
                onPlaceChanged={() => handlePlaceChanged('to')}
              >
                <Input
                  bg={inputBg}
                  placeholder={t('Street address')}
                  value={listingData.toAddress || ''}
                  onChange={(e) =>
                    updateListing({ toAddress: e.target.value, toCoords: null })
                  }
                  onBlur={() => resolveManualCoords('to')}
                  borderRadius="lg"
                  size="md"
                  mb={2}
                />
              </Autocomplete>
            ) : (
              <Input
                bg={inputBg}
                placeholder={t('Street address')}
                value={listingData.toAddress || ''}
                onChange={(e) =>
                  updateListing({ toAddress: e.target.value, toCoords: null })
                }
                borderRadius="lg"
                size="md"
                mb={2}
              />
            )}
            <HStack spacing={2}>
              <Input
                bg={inputBg}
                placeholder={t('City *')}
                value={listingData.toCity || ''}
                onChange={(e) =>
                  updateListing({ toCity: e.target.value, toCoords: null })
                }
                onBlur={() => resolveManualCoords('to')}
                size="sm"
                borderRadius="lg"
                flex="2"
              />
              <Input
                bg={inputBg}
                placeholder={t('Postcode')}
                value={listingData.toPostcode || ''}
                onChange={(e) =>
                  updateListing({ toPostcode: e.target.value, toCoords: null })
                }
                onBlur={() => resolveManualCoords('to')}
                size="sm"
                borderRadius="lg"
                flex="1"
              />
              <Input
                bg={inputBg}
                placeholder={t('Country *')}
                value={listingData.toCountry || ''}
                onChange={(e) =>
                  updateListing({ toCountry: e.target.value, toCoords: null })
                }
                onBlur={() => resolveManualCoords('to')}
                size="sm"
                borderRadius="lg"
                flex="2"
              />
            </HStack>
          </Box>

          {/* Date toggle */}
          <HStack mb={4} spacing={3}>
            <Switch
              isChecked={showDates}
              onChange={(e) => setShowDates(e.target.checked)}
              colorScheme="brand"
              id="dates-toggle"
            />
            <Text
              as="label"
              htmlFor="dates-toggle"
              fontWeight="600"
              fontSize="sm"
              color={labelColor}
              cursor="pointer"
            >
              {t('I want to specify dates')}
            </Text>
          </HStack>

          {showDates && (
            <Stack spacing={4}>
              <Box>
                <Text fontWeight="700" color={labelColor} mb={2} fontSize="sm">
                  {t('Pick-up date range')}
                </Text>
                <RangeDatepicker
                  selectedDates={[
                    listingData.pickupDateFrom
                      ? new Date(listingData.pickupDateFrom)
                      : null,
                    listingData.pickupDateTo
                      ? new Date(listingData.pickupDateTo)
                      : null,
                  ].filter(Boolean)}
                  onDateChange={(dates) => {
                    if (dates.length === 2) {
                      updateListing({
                        pickupDateFrom: format(dates[0], 'yyyy-MM-dd'),
                        pickupDateTo: format(dates[1], 'yyyy-MM-dd'),
                      });
                    } else if (dates.length === 1) {
                      updateListing({
                        pickupDateFrom: format(dates[0], 'yyyy-MM-dd'),
                        pickupDateTo: null,
                      });
                    }
                  }}
                  propsConfigs={{
                    inputProps: {
                      bg: inputBg,
                      size: 'sm',
                      borderRadius: 'lg',
                    },
                    dayOfMonthBtnProps: {
                      defaultBtnProps: {
                        _hover: { bg: ACCENT, color: 'white' },
                      },
                      selectedBtnProps: {
                        bg: ACCENT,
                        color: 'white',
                      },
                    },
                  }}
                  minDate={startOfToday()}
                />
              </Box>

              <Box>
                <Text fontWeight="700" color={labelColor} mb={2} fontSize="sm">
                  {t('Delivery date range')}
                </Text>
                <RangeDatepicker
                  selectedDates={[
                    listingData.deliveryDateFrom
                      ? new Date(listingData.deliveryDateFrom)
                      : null,
                    listingData.deliveryDateTo
                      ? new Date(listingData.deliveryDateTo)
                      : null,
                  ].filter(Boolean)}
                  onDateChange={(dates) => {
                    if (dates.length === 1) {
                      // Check if delivery start is after pickup end
                      const pickupEnd = listingData.pickupDateTo
                        ? new Date(listingData.pickupDateTo)
                        : null;
                      if (pickupEnd && !isAfter(dates[0], pickupEnd)) {
                        toast({
                          title: t('Invalid date'),
                          description: t('Delivery must be after pickup'),
                          status: 'warning',
                          duration: 3000,
                          isClosable: true,
                        });
                        return;
                      }
                    }
                    if (dates.length === 2) {
                      updateListing({
                        deliveryDateFrom: format(dates[0], 'yyyy-MM-dd'),
                        deliveryDateTo: format(dates[1], 'yyyy-MM-dd'),
                      });
                    } else if (dates.length === 1) {
                      updateListing({
                        deliveryDateFrom: format(dates[0], 'yyyy-MM-dd'),
                        deliveryDateTo: null,
                      });
                    }
                  }}
                  propsConfigs={{
                    inputProps: {
                      bg: inputBg,
                      size: 'sm',
                      borderRadius: 'lg',
                    },
                    dayOfMonthBtnProps: {
                      defaultBtnProps: {
                        _hover: { bg: ACCENT, color: 'white' },
                      },
                      selectedBtnProps: {
                        bg: ACCENT,
                        color: 'white',
                      },
                    },
                  }}
                  minDate={
                    listingData.pickupDateTo
                      ? new Date(listingData.pickupDateTo)
                      : startOfToday()
                  }
                />
              </Box>
            </Stack>
          )}

          <Box
            mt={4}
            p={3}
            bg={infoBg}
            border="1px solid"
            borderColor={infoBorderColor}
            borderRadius="xl"
          >
            <Text fontSize="xs" color={mutedText}>
              {t(
                'Add or select pick-up and delivery addresses to preview the Google map route with pins A and B.',
              )}
            </Text>
            {routeDistanceKm || estimatedDistanceKm ? (
              <Text fontSize="sm" fontWeight="700" color={labelColor} mt={2}>
                {t('Route distance: {{distance}} km', {
                  distance: distanceLoading
                    ? '…'
                    : (routeDistanceKm ?? estimatedDistanceKm),
                })}
              </Text>
            ) : null}
          </Box>
        </Box>

        {/* RIGHT: map */}
        <Box
          w={{ base: '100%', lg: '380px' }}
          h={{ base: '220px', lg: '360px' }}
          borderRadius="2xl"
          overflow="hidden"
          flexShrink={0}
          bg="gray.100"
        >
          <Map
            origin={hasRoutePreview ? listingData.fromCoords : null}
            destination={hasRoutePreview ? listingData.toCoords : null}
            zoom={6}
            width={400}
            height={400}
            emptyMessage={t(
              'The map will appear when the pickup and delivery addresses are added.',
            )}
            mapContainerStyle={{ width: '100%', height: '100%' }}
          />
        </Box>
      </Flex>

      {/* Nav */}
      <HStack mt={8} justify="space-between">
        <IconButton
          icon={<Icon as={MdArrowBack} />}
          aria-label={t('Back')}
          borderRadius="full"
          variant="outline"
          onClick={() => setCurrentStep(1)}
        />
        <Button
          variant="brand"
          borderRadius="full"
          px={8}
          fontWeight="700"
          isDisabled={
            !listingData.fromCity ||
            !listingData.fromCountry ||
            !listingData.toCity ||
            !listingData.toCountry
          }
          onClick={handleNext}
        >
          {t('Go to item details')}
        </Button>
      </HStack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Listing Details  (category-aware item form)
// ---------------------------------------------------------------------------

const DEFAULT_VEHICLE_ITEM = {
  make: '',
  model: '',
  year: '',
  vin: '',
  licensePlate: '',
  vehicleStatus: '',
  weight: '',
  quantity: 1,
};

const DEFAULT_ITEM_BY_CATEGORY = {
  cars: { ...DEFAULT_VEHICLE_ITEM },
  motorcycles: { ...DEFAULT_VEHICLE_ITEM },
  other_vehicles: {
    ...DEFAULT_VEHICLE_ITEM,
    length: '',
    width: '',
    height: '',
  },
  boats: { ...DEFAULT_VEHICLE_ITEM, lengthMeters: '' },
  removals: {
    volume: '',
    weight: '',
    originFloor: '',
    destFloor: '',
    hasElevatorOrigin: false,
    hasElevatorDest: false,
  },
  pets: { petType: '', breed: '', weight: '', petDescription: '', quantity: 1 },
  machinery: {
    itemDescription: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    serialNumber: '',
    quantity: 1,
  },
  vehicle_parts: {
    itemDescription: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    serialNumber: '',
    quantity: 1,
  },
  household: {
    itemDescription: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    serialNumber: '',
    quantity: 1,
  },
};

const defaultItemFor = (category) =>
  DEFAULT_ITEM_BY_CATEGORY[category] || {
    itemDescription: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    quantity: 1,
  };

function RequiredMark({ category, field }) {
  if (!isRequiredField(category, field)) return null;
  return (
    <Text as="span" color="red.500" ml={1}>
      *
    </Text>
  );
}

function VehicleItemForm({ category, item, onChange }) {
  const { t } = useTranslation();
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const inputBg = useColorModeValue('white', 'navy.800');
  const isBoat = category === 'boats';
  const showLengthMeters = isBoat;

  return (
    <Stack spacing={4}>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Make')}
            <RequiredMark category={category} field="make" />
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder={t('e.g. BMW')}
            value={item.make || ''}
            onChange={(e) => onChange({ ...item, make: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Model')}
            <RequiredMark category={category} field="model" />
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder={t('e.g. X5')}
            value={item.model || ''}
            onChange={(e) => onChange({ ...item, model: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Year')}
            <RequiredMark category={category} field="year" />
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={1901}
            max={new Date().getFullYear() + 1}
            placeholder="2020"
            value={item.year || ''}
            onChange={(e) => onChange({ ...item, year: e.target.value })}
          />
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {isBoat ? t('HIN (Hull ID)') : t('VIN')}
            <RequiredMark category={category} field="vin" />
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder={isBoat ? 'USBAYX12H021' : 'WBAJA5C53KWW12345'}
            value={item.vin || ''}
            onChange={(e) => onChange({ ...item, vin: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {isBoat ? t('Registration') : t('License plate')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder={isBoat ? 'EU 1234' : 'CA 1234 KX'}
            value={item.licensePlate || ''}
            onChange={(e) => onChange({ ...item, licensePlate: e.target.value })}
          />
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} align={!isBoat ? 'flex-end' : 'stretch'}>
        {!isBoat && (
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
              {t('Vehicle status')}
              <RequiredMark category={category} field="vehicleStatus" />
            </Text>
            <Select
              bg={inputBg}
              size="sm"
              borderRadius="lg"
              value={item.vehicleStatus || ''}
              onChange={(e) => onChange({ ...item, vehicleStatus: e.target.value })}
            >
              <option value="">{t('Choose')}</option>
              <option value="operable">{t('Operable')}</option>
              <option value="non-operable">{t('Non-operable')}</option>
            </Select>
          </Box>
        )}
        {showLengthMeters && (
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
              {t('Length (m)')}
              <RequiredMark category={category} field="lengthMeters" />
            </Text>
            <Input
              bg={inputBg}
              size="sm"
              borderRadius="lg"
              type="number"
              min={0}
              step="0.1"
              placeholder="0"
              value={item.lengthMeters || ''}
              onChange={(e) => onChange({ ...item, lengthMeters: e.target.value })}
            />
          </Box>
        )}
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Weight (kg)')}
            <RequiredMark category={category} field="weight" />
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.weight || ''}
            onChange={(e) => onChange({ ...item, weight: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Qty')}
          </Text>
          <NumberInput
            size="sm"
            min={1}
            value={item.quantity || 1}
            onChange={(val) =>
              onChange({ ...item, quantity: parseInt(val, 10) })
            }
          >
            <NumberInputField borderRadius="lg" bg={inputBg} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>
      </SimpleGrid>
    </Stack>
  );
}

function GeneralItemForm({ category, item, onChange }) {
  const { t } = useTranslation();
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const inputBg = useColorModeValue('white', 'navy.800');
  const showSerialNumber = SERIAL_NUMBER_CATEGORIES.includes(category);

  return (
    <Stack spacing={4}>
      <Box>
        <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
          {t('Description')}
          <RequiredMark category={category} field="itemDescription" />
        </Text>
        <Input
          bg={inputBg}
          size="sm"
          borderRadius="lg"
          placeholder={t('e.g. Wooden dining table')}
          value={item.itemDescription || ''}
          onChange={(e) =>
            onChange({ ...item, itemDescription: e.target.value })
          }
        />
      </Box>
      <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={3}>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Weight (kg)')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.weight || ''}
            onChange={(e) => onChange({ ...item, weight: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('L cm')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.length || ''}
            onChange={(e) => onChange({ ...item, length: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('W cm')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.width || ''}
            onChange={(e) => onChange({ ...item, width: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('H cm')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.height || ''}
            onChange={(e) => onChange({ ...item, height: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Qty')}
          </Text>
          <NumberInput
            size="sm"
            min={1}
            value={item.quantity || 1}
            onChange={(val) =>
              onChange({ ...item, quantity: parseInt(val, 10) })
            }
          >
            <NumberInputField borderRadius="lg" bg={inputBg} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>
      </SimpleGrid>
      {showSerialNumber && (
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Serial number')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder={t('Shown only to the assigned carrier after payment')}
            value={item.serialNumber || ''}
            onChange={(e) => onChange({ ...item, serialNumber: e.target.value })}
          />
        </Box>
      )}
    </Stack>
  );
}

function RemovalsItemForm({ item, onChange }) {
  const { t } = useTranslation();
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const inputBg = useColorModeValue('white', 'navy.800');

  return (
    <Stack spacing={4}>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Total volume (m³)')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.volume || ''}
            onChange={(e) => onChange({ ...item, volume: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Weight (kg)')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.weight || ''}
            onChange={(e) => onChange({ ...item, weight: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Origin floor')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder="0"
            value={item.originFloor || ''}
            onChange={(e) => onChange({ ...item, originFloor: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Dest. floor')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder="0"
            value={item.destFloor || ''}
            onChange={(e) => onChange({ ...item, destFloor: e.target.value })}
          />
        </Box>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6}>
        <HStack>
          <Switch
            size="sm"
            colorScheme="brand"
            isChecked={!!item.hasElevatorOrigin}
            onChange={(e) =>
              onChange({ ...item, hasElevatorOrigin: e.target.checked })
            }
          />
          <Text fontSize="sm">{t('Elevator at origin')}</Text>
        </HStack>
        <HStack>
          <Switch
            size="sm"
            colorScheme="brand"
            isChecked={!!item.hasElevatorDest}
            onChange={(e) =>
              onChange({ ...item, hasElevatorDest: e.target.checked })
            }
          />
          <Text fontSize="sm">{t('Elevator at destination')}</Text>
        </HStack>
      </SimpleGrid>
    </Stack>
  );
}

function PetsItemForm({ item, onChange }) {
  const { t } = useTranslation();
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const inputBg = useColorModeValue('white', 'navy.800');

  return (
    <Stack spacing={4}>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Pet type')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder={t('e.g. Dog')}
            value={item.petType || ''}
            onChange={(e) => onChange({ ...item, petType: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Breed')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            placeholder={t('e.g. Labrador')}
            value={item.breed || ''}
            onChange={(e) => onChange({ ...item, breed: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Weight (kg)')}
          </Text>
          <Input
            bg={inputBg}
            size="sm"
            borderRadius="lg"
            type="number"
            min={0}
            placeholder="0"
            value={item.weight || ''}
            onChange={(e) => onChange({ ...item, weight: e.target.value })}
          />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
            {t('Qty')}
          </Text>
          <NumberInput
            size="sm"
            min={1}
            value={item.quantity || 1}
            onChange={(val) =>
              onChange({ ...item, quantity: parseInt(val, 10) })
            }
          >
            <NumberInputField borderRadius="lg" bg={inputBg} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>
      </SimpleGrid>
      <Box>
        <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>
          {t('Description')}
        </Text>
        <Input
          bg={inputBg}
          size="sm"
          borderRadius="lg"
          placeholder={t('Any special needs or notes')}
          value={item.petDescription || ''}
          onChange={(e) =>
            onChange({ ...item, petDescription: e.target.value })
          }
        />
      </Box>
    </Stack>
  );
}

const VEHICLE_CATEGORIES = ['cars', 'motorcycles', 'other_vehicles', 'boats'];
const REMOVAL_CATEGORIES = ['removals'];
const PET_CATEGORIES = ['pets'];
const SERIAL_NUMBER_CATEGORIES = ['machinery', 'vehicle_parts', 'household'];

function ItemForm({ category, item, onChange }) {
  if (VEHICLE_CATEGORIES.includes(category)) {
    return <VehicleItemForm category={category} item={item} onChange={onChange} />;
  }
  if (REMOVAL_CATEGORIES.includes(category)) {
    return <RemovalsItemForm item={item} onChange={onChange} />;
  }
  if (PET_CATEGORIES.includes(category)) {
    return <PetsItemForm item={item} onChange={onChange} />;
  }
  return <GeneralItemForm category={category} item={item} onChange={onChange} />;
}

function Step3Details() {
  const { listingData, updateListing, setCurrentStep } = useListing();
  const { t } = useTranslation();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const inputBg = useColorModeValue('white', 'navy.800');
  const cardBg = useColorModeValue('gray.50', 'navy.900');
  const uploadPanelBg = useColorModeValue('white', 'whiteAlpha.60');
  const uploadPanelBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const uploadDropBg = useColorModeValue('blue.50', 'whiteAlpha.120');
  const uploadDropBorder = useColorModeValue('blue.200', 'blue.300');
  const uploadFileCardBg = useColorModeValue('white', 'whiteAlpha.100');

  const [showDescription, setShowDescription] = useState(
    !!listingData.description,
  );
  const [showImages, setShowImages] = useState(false);
  const fileInputRef = useRef(null);

  const items =
    listingData.items.length > 0
      ? listingData.items
      : [defaultItemFor(listingData.category)];

  const updateItem = (index, updated) => {
    const next = [...items];
    next[index] = updated;
    updateListing({ items: next });
  };

  const addItem = () => {
    updateListing({
      items: [...items, defaultItemFor(listingData.category)],
    });
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    updateListing({ items: items.filter((_, i) => i !== index) });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    updateListing({ images: [...listingData.images, ...files].slice(0, 10) });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    updateListing({
      images: listingData.images.filter(
        (_, currentIndex) => currentIndex !== index,
      ),
    });
  };

  const categoryLabel =
    CATEGORIES.find((c) => c.id === listingData.category)?.label ||
    listingData.category;
  const clientPricePreview = formatCurrency(listingData.clientPriceGross);

  return (
    <Box>
      <Heading size="lg" fontWeight="800" mb={1}>
        {t('Listing details')}
      </Heading>
      <Text color={mutedText} mb={8} fontSize="md">
        {t('Category: {{category}}', { category: t(categoryLabel) })}
      </Text>

      {/* Items */}
      <Stack spacing={4} mb={6}>
        {items.map((item, idx) => (
          <Box key={idx} bg={cardBg} borderRadius="xl" p={4}>
            <Flex align="center" justify="space-between" mb={3}>
              <Text fontWeight="700" fontSize="sm" color={labelColor}>
                {t('Item {{index}}', { index: idx + 1 })}
              </Text>
              {items.length > 1 && (
                <IconButton
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  icon={<Icon as={MdDeleteOutline} />}
                  aria-label={t('Remove item')}
                  onClick={() => removeItem(idx)}
                />
              )}
            </Flex>
            <ItemForm
              category={listingData.category}
              item={item}
              onChange={(updated) => updateItem(idx, updated)}
            />
          </Box>
        ))}
      </Stack>

      {/* Add another */}
      <Button
        variant="link"
        color="brand.500"
        leftIcon={<Icon as={MdAdd} />}
        fontWeight="700"
        fontSize="sm"
        mb={7}
        onClick={addItem}
      >
        {t('+ Add another item')}
      </Button>

      {/* Client type */}
      <Box mb={6}>
        <Text fontWeight="700" color={labelColor} mb={3} fontSize="sm">
          {t('I am listing delivery as')}
        </Text>
        <RadioGroup
          value={listingData.clientType}
          onChange={(val) => updateListing({ clientType: val })}
          colorScheme="brand"
        >
          <HStack spacing={6}>
            <Radio value="private">{t('Private person')}</Radio>
            <Radio value="company">{t('A company')}</Radio>
          </HStack>
        </RadioGroup>
        {listingData.clientType === 'company' && (
          <Box mt={3}>
            <Input
              bg={inputBg}
              size="sm"
              borderRadius="lg"
              placeholder={t('Company country')}
              value={listingData.companyCountry}
              onChange={(e) =>
                updateListing({ companyCountry: e.target.value })
              }
              maxW="260px"
            />
          </Box>
        )}
      </Box>

      <Box mb={6}>
        <Text fontWeight="700" color={labelColor} mb={3} fontSize="sm">
          {t('Pricing and quote policy')}
        </Text>
        <Stack spacing={4}>
          <Box
            border="1px solid"
            borderColor={uploadPanelBorder}
            borderRadius="20px"
            p={4}
            bg={cardBg}
          >
            <HStack justify="space-between" align="flex-start" spacing={4}>
              <Box>
                <Text fontSize="sm" fontWeight="700" color={labelColor}>
                  {t('Allow partner quotes')}
                </Text>
                <Text fontSize="xs" color={mutedText} mt={1}>
                  {t(
                    'Turn this off to publish a fixed-price listing that partners can accept directly.',
                  )}
                </Text>
              </Box>
              <Switch
                colorScheme="brand"
                isChecked={listingData.allowsQuotes !== false}
                onChange={(event) =>
                  updateListing({ allowsQuotes: event.target.checked })
                }
              />
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
              {t('Client price visible to partners')}
            </Text>
            <Input
              bg={inputBg}
              type="number"
              min={0}
              step="0.01"
              borderRadius="lg"
              placeholder={t('Enter amount in EUR')}
              value={listingData.clientPriceGross}
              onChange={(e) =>
                updateListing({ clientPriceGross: e.target.value })
              }
              maxW="280px"
            />
            <Text fontSize="xs" color={mutedText} mt={2}>
              {listingData.allowsQuotes !== false
                ? t(
                    'Optional. Partners will see this as your target budget, but they can still send their own quote.',
                  )
                : t(
                    'Required. Partners will be able to accept this listing at the fixed client price without sending a quote.',
                  )}
            </Text>
            {clientPricePreview ? (
              <Text fontSize="sm" fontWeight="700" color={labelColor} mt={2}>
                {t('Visible price: {{price}}', { price: clientPricePreview })}
              </Text>
            ) : null}
          </Box>
        </Stack>
      </Box>

      {/* Description toggle */}
      <Box mb={4}>
        <Button
          variant="link"
          color={labelColor}
          fontWeight="600"
          fontSize="sm"
          onClick={() => setShowDescription((p) => !p)}
        >
          {showDescription
            ? t('Hide description')
            : t('Add description (optional)')}
        </Button>
        {showDescription && (
          <Textarea
            mt={3}
            bg={inputBg}
            borderRadius="lg"
            placeholder={t('Describe your shipment…')}
            rows={4}
            value={listingData.description}
            onChange={(e) => updateListing({ description: e.target.value })}
          />
        )}
      </Box>

      {/* Images toggle */}
      <Box mb={8}>
        <Button
          variant="link"
          color={labelColor}
          fontWeight="600"
          fontSize="sm"
          onClick={() => {
            setShowImages((p) => !p);
            if (!showImages) fileInputRef.current?.click();
          }}
        >
          {t('Add images (optional)')}
        </Button>
        {showImages && (
          <Box mt={3}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            <Box
              bg={uploadPanelBg}
              border="1px solid"
              borderColor={uploadPanelBorder}
              borderRadius="20px"
              p={{ base: 4, md: 5 }}
            >
              <VStack
                spacing={4}
                align="stretch"
                bg={uploadDropBg}
                border="1px dashed"
                borderColor={uploadDropBorder}
                borderRadius="16px"
                p={{ base: 4, md: 5 }}
              >
                <HStack spacing={3} align="flex-start">
                  <Icon
                    as={FiUploadCloud}
                    boxSize={5}
                    color="brand.400"
                    mt={0.5}
                  />
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color={labelColor}>
                      {t('Upload shipment photos')}
                    </Text>
                    <Text fontSize="xs" color={mutedText} mt={1}>
                      {t(
                        'Add up to 10 images. JPG and PNG files work best for listing previews.',
                      )}
                    </Text>
                  </Box>
                </HStack>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="brand"
                  alignSelf="flex-start"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('Choose images')}
                </Button>
              </VStack>

              {listingData.images.length > 0 && (
                <VStack align="stretch" spacing={3} mt={4}>
                  {listingData.images.map((file, index) => (
                    <Flex
                      key={`${file.name}-${file.size}-${index}`}
                      bg={uploadFileCardBg}
                      border="1px solid"
                      borderColor={uploadPanelBorder}
                      borderRadius="14px"
                      px={3}
                      py={2.5}
                      justify="space-between"
                      align="center"
                      gap={3}
                    >
                      <HStack spacing={3} minW={0}>
                        <Icon as={FiFileText} color="brand.400" boxSize={4} />
                        <Box minW={0}>
                          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                            {file.name}
                          </Text>
                          <Text fontSize="xs" color={mutedText}>
                            {formatFileSize(file.size)}
                          </Text>
                        </Box>
                      </HStack>
                      <IconButton
                        icon={<Icon as={FiX} />}
                        aria-label={t('Remove {{name}}', { name: file.name })}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => removeImage(index)}
                      />
                    </Flex>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Nav */}
      <HStack justify="space-between">
        <IconButton
          icon={<Icon as={MdArrowBack} />}
          aria-label={t('Back')}
          borderRadius="full"
          variant="outline"
          onClick={() => setCurrentStep(2)}
        />
        <Button
          variant="brand"
          borderRadius="full"
          px={8}
          fontWeight="700"
          onClick={() => setCurrentStep(4)}
        >
          {t('Go to summary')}
        </Button>
      </HStack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Summary
// ---------------------------------------------------------------------------

function SummarySection({ title, onEdit, children }) {
  const { t } = useTranslation();
  const cardBg = useColorModeValue('gray.50', 'navy.900');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      p={5}
    >
      <Flex align="center" justify="space-between" mb={4}>
        <Text fontWeight="800" fontSize="md">
          {t(title)}
        </Text>
        <Button
          variant="ghost"
          size="xs"
          color={ACCENT}
          leftIcon={<Icon as={MdEdit} boxSize={3} />}
          fontWeight="700"
          onClick={onEdit}
        >
          {t('Edit')}
        </Button>
      </Flex>
      {children}
    </Box>
  );
}

function LocationLine({ label, address, city, country, dates }) {
  const { t } = useTranslation();
  const mutedText = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box mb={3}>
      <Text
        fontSize="xs"
        fontWeight="700"
        color={mutedText}
        textTransform="uppercase"
        letterSpacing="wide"
        mb={0.5}
      >
        {t(label)}
      </Text>
      <Text fontWeight="700">
        {[address, city, country].filter(Boolean).join(', ') || '—'}
      </Text>
      {dates && (
        <Text fontSize="xs" color={mutedText}>
          {dates}
        </Text>
      )}
    </Box>
  );
}

function formatDateRange(from, to) {
  if (!from && !to) return null;
  if (from && to) return `${from} — ${to}`;
  return from || to;
}

function Step4Summary() {
  const { listingData, updateListing, setCurrentStep, resetListing } =
    useListing();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const inputBg = useColorModeValue('white', 'navy.800');
  const progressTrackColor = useColorModeValue('blue.50', 'whiteAlpha.200');
  const pricingSummaryBg = useColorModeValue('blue.50', 'whiteAlpha.80');
  const hasRoutePreview = Boolean(
    listingData.fromCoords && listingData.toCoords,
  );
  const estimatedDistanceKm = useMemo(
    () => estimateDistanceKm(listingData.fromCoords, listingData.toCoords),
    [listingData.fromCoords, listingData.toCoords],
  );
  const clientPricePreview = formatCurrency(listingData.clientPriceGross);

  const [submitting, setSubmitting] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitProgress, setSubmitProgress] = useState(0);

  const categoryInfo = CATEGORIES.find((c) => c.id === listingData.category);
  const imagePreviews = useMemo(
    () =>
      listingData.images.map((file) => ({
        key: fileIdentity(file),
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [listingData.images],
  );

  useEffect(
    () => () => {
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    },
    [imagePreviews],
  );

  const handleSubmit = async () => {
    if (listingData.allowsQuotes === false && !clientPricePreview) {
      toast({
        title: t('Set a fixed price'),
        description: t(
          'A fixed-price listing needs a visible client price before publishing.',
        ),
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Normalize wizard items into the backend payload shape *before* validating
    // so client-side rules match what the server will enforce on publish.
    const normalizedItems = Array.isArray(listingData.items)
      ? listingData.items.map(normalizeItemForPayload)
      : [];
    const validation = validateItemsForCategory(listingData.category, normalizedItems);
    if (!validation.valid) {
      toast({
        title: t('Missing required item details'),
        description: formatValidationErrors(validation),
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    setSubmitMessage(t('Preparing listing draft...'));
    setSubmitProgress(12);
    try {
      const draftPayload = buildDraftPayload(listingData);
      let draftId = listingData.draftListingId;

      if (draftId) {
        try {
          await updateListingRequest(draftId, draftPayload);
        } catch {
          draftId = null;
          updateListing({ draftListingId: null, uploadedImageDocs: [] });
        }
      }

      if (!draftId) {
        const draft = await createListingDraft(draftPayload);
        draftId = draft.id || draft._id;
        updateListing({ draftListingId: draftId });
      }

      setSubmitProgress(28);

      const selectedKeys = new Set(
        listingData.images.map((file) => fileIdentity(file)),
      );
      let uploadedImageDocs = Array.isArray(listingData.uploadedImageDocs)
        ? listingData.uploadedImageDocs.filter((doc) =>
            selectedKeys.has(doc.fileKey),
          )
        : [];

      if (
        uploadedImageDocs.length !==
        (listingData.uploadedImageDocs || []).length
      ) {
        updateListing({ uploadedImageDocs });
      }

      const remainingFiles = listingData.images.filter(
        (file) =>
          !uploadedImageDocs.some((doc) => doc.fileKey === fileIdentity(file)),
      );

      for (let index = 0; index < remainingFiles.length; index += 1) {
        const file = remainingFiles[index];
        setSubmitMessage(
          t('Uploading image {{index}} of {{total}}...', {
            index: index + 1,
            total: remainingFiles.length,
          }),
        );
        setSubmitProgress(
          28 +
            Math.round(((index + 1) / Math.max(remainingFiles.length, 1)) * 48),
        );
        const uploadedImage = await uploadListingImage(file, draftId);
        uploadedImageDocs = [
          ...uploadedImageDocs,
          {
            ...uploadedImage,
            fileKey: fileIdentity(file),
          },
        ];
        updateListing({ uploadedImageDocs, draftListingId: draftId });
      }

      setSubmitMessage(t('Publishing listing...'));
      setSubmitProgress(88);
      await publishListingDraft(
        draftId,
        uploadedImageDocs.map((doc) => doc.documentId),
      );
      resetListing();
      toast({
        title: t('Listing created!'),
        description:
          listingData.allowsQuotes === false
            ? t(
                'Your fixed-price listing is live. Partners can now accept it directly.',
              )
            : t('Your listing is live. Carriers will send quotes soon.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      navigate('/my-listings');
    } catch (err) {
      setSubmitMessage('');
      setSubmitProgress(0);
      toast({
        title: t('Could not create listing'),
        description:
          err?.response?.data?.message || err.message || t('Unknown error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Flex
        align="center"
        justify="space-between"
        mb={2}
        flexWrap="wrap"
        gap={3}
      >
        <Box>
          <Heading size="lg" fontWeight="800" mb={0.5}>
            {t('Summary')}
          </Heading>
          <Text color={mutedText} fontSize="sm">
            {t('List a delivery for free and with no commitment')}
          </Text>
        </Box>
      </Flex>

      {submitting ? (
        <Box
          mt={5}
          mb={2}
          p={4}
          borderRadius="xl"
          bg={progressTrackColor}
          border="1px solid"
          borderColor="blue.100"
        >
          <Text fontSize="sm" fontWeight="700" color={labelColor} mb={2}>
            {submitMessage}
          </Text>
          <Progress
            value={submitProgress}
            size="sm"
            colorScheme="brand"
            borderRadius="full"
          />
        </Box>
      ) : null}

      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        gap={5}
        mt={8}
        mb={6}
      >
        {/* Addresses & Dates */}
        <SummarySection
          title="Addresses and dates"
          onEdit={() => setCurrentStep(2)}
        >
          <LocationLine
            label="Pick-up"
            address={listingData.fromAddress}
            city={listingData.fromCity}
            country={listingData.fromCountry}
            dates={formatDateRange(
              listingData.pickupDateFrom,
              listingData.pickupDateTo,
            )}
          />
          <LocationLine
            label="Delivery"
            address={listingData.toAddress}
            city={listingData.toCity}
            country={listingData.toCountry}
            dates={formatDateRange(
              listingData.deliveryDateFrom,
              listingData.deliveryDateTo,
            )}
          />
          {estimatedDistanceKm ? (
            <Text fontSize="sm" fontWeight="700" color={labelColor} mb={4}>
              {t('Route distance: {{distance}} km', {
                distance: estimatedDistanceKm,
              })}
            </Text>
          ) : null}
          <Box
            mt={4}
            h={{ base: '220px', md: '280px' }}
            borderRadius="xl"
            overflow="hidden"
            bg="gray.100"
          >
            <Map
              origin={hasRoutePreview ? listingData.fromCoords : null}
              destination={hasRoutePreview ? listingData.toCoords : null}
              zoom={6}
              width={640}
              height={320}
              fitBoundsPadding={64}
              maxAutoZoom={11}
              emptyMessage={t(
                'The map will appear when the pickup and delivery addresses are added.',
              )}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            />
          </Box>
        </SummarySection>

        {/* Item details */}
        <SummarySection title="Item details" onEdit={() => setCurrentStep(3)}>
          <ListingItemsSection
            items={listingData.items}
            category={listingData.category}
            subtitle={t(
              'Review each item, quantity and handling status before publishing.',
            )}
          />
          <Text fontSize="sm" color={mutedText}>
            {listingData.clientType === 'company'
              ? t('Company ({{country}})', {
                  country: listingData.companyCountry || t('country not set'),
                })
              : t('Private person')}
          </Text>
          {listingData.description && (
            <Text fontSize="sm" color={mutedText} mt={2} noOfLines={3}>
              {listingData.description}
            </Text>
          )}
          {listingData.images.length > 0 && (
            <Box mt={3}>
              <Text
                fontSize="xs"
                fontWeight="700"
                color={mutedText}
                textTransform="uppercase"
                mb={2}
              >
                {t('Attached images ({{count}})', {
                  count: listingData.images.length,
                })}
              </Text>
              <Grid
                templateColumns={{
                  base: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                }}
                gap={3}
              >
                {imagePreviews.map((preview) => (
                  <Box
                    key={preview.key}
                    borderRadius="14px"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="brand.100"
                    bg="white"
                  >
                    <Image
                      src={preview.url}
                      alt={preview.name}
                      w="100%"
                      h="96px"
                      objectFit="cover"
                    />
                    <Text
                      fontSize="xs"
                      color={mutedText}
                      px={2.5}
                      py={2}
                      noOfLines={1}
                    >
                      {preview.name}
                    </Text>
                  </Box>
                ))}
              </Grid>
            </Box>
          )}
        </SummarySection>
      </Grid>

      <Box
        mb={8}
        border="1px solid"
        borderColor="brand.100"
        borderRadius="24px"
        p={5}
        bg={pricingSummaryBg}
      >
        <Text
          fontSize="10px"
          fontWeight="800"
          color={ACCENT}
          textTransform="uppercase"
          letterSpacing="0.08em"
          mb={2}
        >
          {t('Pricing')}
        </Text>
        <Text fontSize="md" fontWeight="800" color={labelColor}>
          {clientPricePreview || t('No visible client price set')}
        </Text>
        <Text fontSize="sm" color={mutedText} mt={1}>
          {listingData.allowsQuotes === false
            ? t(
                'Quotes are disabled. The first partner to accept this price will be assigned automatically.',
              )
            : t(
                'Quotes are enabled. Partners can see this price as a target budget and still send their own offers.',
              )}
        </Text>
      </Box>

      {/* Category + Title */}
      <Box mb={8}>
        <HStack spacing={4} mb={4} flexWrap="wrap">
          <Box
            bg={ACCENT_SUBTLE_BG}
            border="1px solid"
            borderColor={ACCENT_BORDER}
            borderRadius="full"
            px={4}
            py={1}
          >
            <Text fontSize="sm" fontWeight="700" color={ACCENT}>
              {categoryInfo?.emoji}{' '}
              {categoryInfo?.label && t(categoryInfo.label)}
            </Text>
          </Box>
        </HStack>

        <Flex align="center" gap={3}>
          {editingTitle ? (
            <Input
              autoFocus
              bg={inputBg}
              borderRadius="lg"
              fontWeight="700"
              fontSize="md"
              value={listingData.title}
              placeholder={t('Give your listing a title (optional)')}
              onChange={(e) => updateListing({ title: e.target.value })}
              onBlur={() => setEditingTitle(false)}
              onKeyDown={(e) => e.key === 'Enter' && setEditingTitle(false)}
              maxW="480px"
            />
          ) : (
            <>
              <Text fontWeight="700" fontSize="md" color={labelColor}>
                {listingData.title || t('Add a listing title (optional)')}
              </Text>
              <IconButton
                icon={<Icon as={MdEdit} />}
                size="xs"
                variant="ghost"
                colorScheme="brand"
                aria-label={t('Edit title')}
                onClick={() => setEditingTitle(true)}
              />
            </>
          )}
        </Flex>
      </Box>

      {/* Nav */}
      <HStack justify="space-between">
        <IconButton
          icon={<Icon as={MdArrowBack} />}
          aria-label={t('Back')}
          borderRadius="full"
          variant="outline"
          onClick={() => setCurrentStep(3)}
        />
        <Button
          variant="brand"
          borderRadius="full"
          px={10}
          fontWeight="700"
          isLoading={submitting}
          loadingText={t('Publishing...')}
          onClick={handleSubmit}
        >
          {listingData.allowsQuotes === false
            ? t('Publish Fixed Price')
            : t('Get Quotes')}
        </Button>
      </HStack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Wizard shell
// ---------------------------------------------------------------------------

const STEP_LABELS = [
  'Select category',
  'Choose route',
  'Listing details',
  'Summary',
];

const STEP_COMPONENTS = [Step1Category, Step2Route, Step3Details, Step4Summary];

export default function CreateListing() {
  const { currentStep, resetListing, updateListing, setCurrentStep } = useListing();
  const { t } = useTranslation();
  const bg = useColorModeValue('rgba(255,255,255,0.90)', 'rgba(15,28,63,0.72)');
  const pageBg = useColorModeValue('gray.50', 'navy.900');
  const panelBorderColor = useColorModeValue(
    'rgba(255,255,255,0.95)',
    'rgba(255,255,255,0.16)',
  );
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const [addressAutocompleteEnabled, setAddressAutocompleteEnabled] = useState(true);
  // Load Maps JS once with a stable config. The `addressAutocompleteEnabled` flag
  // gates the Autocomplete widget at render time; un-rendered widgets cost nothing.
  const { isLoaded: isGoogleLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  useEffect(() => {
    let cancelled = false;
    getPublicSettings()
      .then((settings) => {
        if (cancelled) return;
        setAddressAutocompleteEnabled(settings?.addressAutocompleteEnabled !== false);
      })
      .catch(() => {
        // Silent: keep enabled by default if settings fetch fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const StepComponent = STEP_COMPONENTS[currentStep - 1];

  useEffect(() => {
    resetListing();
    const aiPrefill = consumeClientListingPrefill();
    if (!aiPrefill) {
      return;
    }

    updateListing(aiPrefill);
    if (aiPrefill.category) {
      setCurrentStep(2);
    }
  }, [resetListing, setCurrentStep, updateListing]);

  return (
    <Box minH="100vh" bg={pageBg}>
      <PageShell>
        <Box w="100%" mx="auto">
          <PageHeader
            title={t('Create Listing')}
            description={t(
              'Publish a transport request using the same structured workflow used across partner operations.',
            )}
            actions={
              <AiAssistantDrawer
                role="client"
                screenContext={{
                  routeKey: 'client.create-listing',
                  role: 'client',
                  surface: 'create-listing',
                }}
                buttonLabel={t('Draft with AI')}
                subtitle={t('Describe the route and cargo, then review the prepared listing draft before publishing.')}
                onHandoff={(handoffRoute) => {
                  if (handoffRoute?.routeKey !== 'client.listings.create') {
                    return false;
                  }

                  const prefill = handoffRoute?.params?.prefill || {};
                  updateListing(prefill);
                  if (prefill?.category) {
                    setCurrentStep(2);
                  }
                  return true;
                }}
              />
            }
          />
          <Box
            mt={6}
            bg={bg}
            border="1px solid"
            borderColor={panelBorderColor}
            borderRadius="2xl"
            p={{ base: 5, md: 8 }}
            backdropFilter="blur(10px)"
            boxShadow="0 24px 60px rgba(9, 30, 66, 0.16)"
          >
            {/* Step dots */}
            <StepDots current={currentStep} total={4} />

            {/* Step label row */}
            <HStack
              spacing={0}
              justify="space-between"
              mb={8}
              display={{ base: 'none', md: 'flex' }}
            >
              {STEP_LABELS.map((label, i) => {
                const step = i + 1;
                const done = step < currentStep;
                const active = step === currentStep;
                return (
                  <Flex key={step} align="center" flex="1">
                    <VStack spacing={1} align="center" flex="1">
                      <Box
                        w="28px"
                        h="28px"
                        borderRadius="full"
                        bg={done || active ? ACCENT : 'gray.200'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="900"
                          color={done || active ? 'white' : 'gray.500'}
                        >
                          {step}
                        </Text>
                      </Box>
                      <Text
                        fontSize="xs"
                        fontWeight={active ? '800' : '500'}
                        color={active ? ACCENT : mutedText}
                        textAlign="center"
                      >
                        {t(label)}
                      </Text>
                    </VStack>
                    {i < STEP_LABELS.length - 1 && (
                      <Box
                        flex="1"
                        h="2px"
                        bg={currentStep > step ? ACCENT : 'gray.200'}
                        mt="-14px"
                        mx={1}
                      />
                    )}
                  </Flex>
                );
              })}
            </HStack>

            {/* Active step */}
            <StepComponent
              isGoogleLoaded={isGoogleLoaded}
              addressAutocompleteEnabled={addressAutocompleteEnabled}
            />
          </Box>
        </Box>
      </PageShell>
    </Box>
  );
}
